import { useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../lib/siteUrl';

const DEFAULT_IMAGE = `${SITE_URL}/og-banner.png`;
const DEFAULT_DESC = 'Agence is an AI personal finance copilot. Connect your bank and brokerage, get AI budget insights, investment analysis, and rule-based trading autopilot.';

export default function PageMeta({ title, titleOverride, description, path, schema, type = 'website', publishedTime, modifiedTime }) {
  // Subpages use page-first "{title} — Agence" for keyword relevance; the
  // homepage passes its own titleOverride (also keyword-first, per SEO positioning).
  const fullTitle = titleOverride || (title ? `${title} — Agence` : 'Agence — AI-Powered Finance & Investment Copilot');
  const desc = description || DEFAULT_DESC;
  const url = `${SITE_URL}${path || ''}`;

  // Drop the generic static fallback block from public/index.html once real per-page tags
  // exist. react-helmet-async@3 runs React 19's native metadata hoisting, which appends
  // tags and never reconciles against anything already in <head> — so without this every
  // route renders two titles, two descriptions and two canonicals, the first canonical
  // being the homepage on every page. See `project-memory/context/decisions/frontend-and-ux.md`.
  //
  // Scoped to PageMeta rather than the app root on purpose: a route that renders no
  // PageMeta (e.g. /login) then keeps the fallback instead of ending up with no metadata
  // at all. Runs after the Helmet tags are committed, so there is never a frame with none.
  //
  // useLayoutEffect, not useEffect, and that matters. React 19 hoists the Helmet tags during
  // the commit, but a passive effect was scheduled into a LATER one — measured at a ~56ms
  // window in which the document really did carry two titles and two canonicals, the first
  // canonical being the homepage. Any crawler or observer sampling inside that window saw
  // duplicates; it is also what made page-meta.spec.js fail roughly 1 run in 336. A layout
  // effect fires in the same commit, before paint, so no frame is ever observable with both.
  useLayoutEffect(() => {
    document.querySelectorAll('head [data-static-meta]').forEach(node => node.remove());
  }, []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta property="og:site_name" content="Agence" />
      {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {type === 'article' && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter / X card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@agencefinance" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />

      {/* Structured data */}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}
